import "./ProjectDetailsWizard.css";

import { useEffect, useState } from "react";

import { ProjectDetailsWizardSection } from "./components";
import { DEFAULT_PROJECT_DETAILS_WIZARD_TITLE } from "./constants";
import { ProjectDetailsWizardProps } from "./models";
import { areStepKeysEqual, getCurrentStepKeys, getInitialExpandedStepKey, isStepCurrent } from "./utils";

export const ProjectDetailsWizard = ({
  title = DEFAULT_PROJECT_DETAILS_WIZARD_TITLE,
  steps,
  handleMoveToStep,
}: ProjectDetailsWizardProps): JSX.Element => {
  const [expandedStepKey, setExpandedStepKey] = useState<string | undefined>(() => getInitialExpandedStepKey(steps));
  const [selectedSubStep, setSelectedSubStep] = useState<string[] | undefined>(() => getCurrentStepKeys(steps));

  useEffect(() => {
    const currentStepKeys = getCurrentStepKeys(steps);

    setSelectedSubStep((previousSelectedSubStep) => {
      if (areStepKeysEqual(previousSelectedSubStep, currentStepKeys)) {
        return previousSelectedSubStep;
      }

      return currentStepKeys;
    });

    setExpandedStepKey((previousExpandedStepKey) => {
      if (currentStepKeys) {
        const selectedStep = steps.find((step) => step.key === currentStepKeys[0]);

        if (selectedStep?.subSteps.length) {
          return selectedStep.key;
        }

        return undefined;
      }

      if (previousExpandedStepKey && steps.some((step) => step.key === previousExpandedStepKey)) {
        return previousExpandedStepKey;
      }

      return getInitialExpandedStepKey(steps);
    });
  }, [steps]);

  const onStepSelect = (stepKey: string): void => {
    if (areStepKeysEqual(selectedSubStep, [stepKey])) {
      return;
    }

    if (handleMoveToStep) {
      handleMoveToStep([stepKey]);
      return;
    }

    setSelectedSubStep([stepKey]);
  };

  const onSubStepSelect = (stepKey: string, subStepKey: string): void => {
    if (areStepKeysEqual(selectedSubStep, [stepKey, subStepKey])) {
      return;
    }

    if (handleMoveToStep) {
      handleMoveToStep([stepKey, subStepKey]);
      return;
    }

    setSelectedSubStep([stepKey, subStepKey]);
  };

  const onStepExpandToggle = (stepKey: string): void => {
    setExpandedStepKey((previousExpandedStepKey) => (previousExpandedStepKey === stepKey ? undefined : stepKey));
  };

  return (
    <div className="ProjectDetailsWizard">
      <h2>{title}</h2>
      <div className="ProjectDetailsWizardSections">
        {steps.map((step, index) => (
          <ProjectDetailsWizardSection
            key={step.key}
            step={step}
            stepNumber={index + 1}
            isCurrent={isStepCurrent(step) || selectedSubStep?.[0] === step.key}
            isExpanded={expandedStepKey === step.key}
            selectedSubStep={selectedSubStep}
            onStepSelect={onStepSelect}
            onStepExpandToggle={onStepExpandToggle}
            onSubStepSelect={onSubStepSelect}
          />
        ))}
      </div>
    </div>
  );
};
